import { FormEvent, useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from "@jourfx/ui";
import { MessageSquare, Send } from "lucide-react";

type Id<T extends string> = string & { __tableName: T };

interface WaitingRoomChatPanelProps {
  appointmentId: string;
  compact?: boolean;
}

export function WaitingRoomChatPanel({
  appointmentId,
  compact = false,
}: WaitingRoomChatPanelProps) {
  const apiRef = api as any;
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  const messages = useQuery(apiRef.waitingRoomChat.index.listForStaff, {
    appointmentId: appointmentId as Id<"appointments">,
  }) as
    | Array<{
        _id: string;
        senderType: "versicherter" | "staff";
        senderName: string;
        message: string;
        createdAt: number;
      }>
    | undefined;

  const sendAsStaff = useMutation(apiRef.waitingRoomChat.index.sendAsStaff);

  const sortedMessages = useMemo(
    () => [...(messages || [])].sort((a, b) => a.createdAt - b.createdAt),
    [messages]
  );

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const text = message.trim();
    if (!text) return;

    setIsSending(true);
    try {
      await sendAsStaff({
        appointmentId: appointmentId as Id<"appointments">,
        message: text,
      });
      setMessage("");
    } catch (error) {
      console.error("Failed to send waiting room chat message", error);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Card>
      <CardHeader className={compact ? "pb-3" : undefined}>
        <CardTitle className="text-base flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Warteraum-Chat
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className={`${compact ? "max-h-40" : "max-h-64"} overflow-y-auto rounded-md border bg-muted/20 p-3 space-y-2`}>
          {messages === undefined && (
            <p className="text-sm text-muted-foreground">Lade Nachrichten...</p>
          )}
          {messages && messages.length === 0 && (
            <p className="text-sm text-muted-foreground">Noch keine Nachrichten aus dem Warteraum.</p>
          )}
          {sortedMessages.map((entry) => (
            <div key={entry._id} className="rounded border bg-background p-2">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                {entry.senderName}
              </div>
              <div className="text-sm">{entry.message}</div>
            </div>
          ))}
        </div>

        <form className="flex gap-2" onSubmit={handleSubmit}>
          <Input
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Nachricht an Versicherte oder Team..."
          />
          <Button type="submit" size="sm" disabled={isSending || !message.trim()}>
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

